/**
 * Back closes whatever is open — a drawer, sheet or modal — instead of leaving
 * the page underneath it.
 *
 * Nothing that opens over the page told the browser's history about it, so Back
 * (the phone's button, the app's, the browser's) went to the previous page: bag
 * drawer open on a product page, press Back, land on the collection page.
 *
 * Rather than teach two dozen drawers about history one by one, this recognises
 * an open layer three ways and keeps one extra history entry per open layer:
 *   1. a modal <dialog> with the `open` attribute — every showModal() caller and
 *      every <dialog-component> (bag drawer, filters, bottom sheets);
 *   2. the sheets listed in SHEETS, which are toggled by a class;
 *   3. failing both, <body> being scroll-locked, which is what any other
 *      class-toggled overlay does (reviews form, video popup, …).
 * Back steps off an entry and closes the top layer through what that layer
 * already supports: its own close method, closeDialog() on a <dialog-component>,
 * or the Escape key the class-toggled ones all listen for.
 *
 * History is only added to when a layer opens. Closing a layer with its own
 * button leaves its entry where it is, as a spare: the next layer reuses it, and
 * a Back that finds nothing open on a spare carries on to the page before.
 * Stepping back on every close instead (an earlier version did) raced whatever
 * else the same tap started: in Safari it cancelled the navigation of a button
 * that closes its sheet and then goes to checkout, and it took the address a
 * filter had just written with it.
 *
 * The one place a spare is handed back is the first page of a visit, where
 * nothing lies behind the page for that Back to carry on to: the app would be
 * told there is still somewhere to go back to, and the press that should leave
 * it would do nothing. See giveBack().
 *
 * The old bag drawer (cart-drawer-component, assets/cart-drawer.js) keeps its own
 * history handling and is left alone.
 *
 * The app's exit warning (snippets/includ-exit-toast.liquid) rides on this too:
 * while it is switched on, one entry is kept above the home page so that the
 * first Back there has somewhere to land and can show the warning.
 */
(function () {
  var KEY = 'includOverlay'; // on our entries: how many of them end here (0 = the page's own entry, 1 = first above it)

  /** Class-toggled sheets: [selector that matches while open, how to close it (omit = Escape)]. */
  var SHEETS = [
    ['.cart-v2-coupon-drawer--open', function (el) { el.closest('cart-drawer-v2').closeCouponDrawer(); }],
    ['[data-sd-drawer].is-open'],
    ['.category-drawer--open'],
    ['.ssc-modal.active'],
    ['quick-shop-modal.is-open'],
  ];
  /** On <body> while Back on the first page of a visit should warn before leaving (snippets/includ-exit-toast.liquid). */
  var GUARD = 'includ-exit-guard';
  /** Elements whose class changes can open or close one of the above. */
  var WATCH = 'cart-drawer-v2, [data-sd-drawer], .category-drawer-section, .ssc-modal, quick-shop-modal';

  var order = []; // open modal dialogs, oldest first
  var closing = new Set(); // dialogs Back is closing; still `open` while they animate out
  var watched = new WeakSet();
  var deaf = false; // the page is scroll-locked by something that did not close on Escape
  var giving = false; // our own step back off a spare is on its way (first page of a visit only)
  var giveTimer = 0;
  var leavingAt = 0; // when a navigation to another page was last seen starting
  var mode = 'auto'; // the page's own scroll restoration, for the entry the shopper is on
  var held = false; // in the app: hold() has fixed <html> in place under an open layer
  var heldY = 0; // where the page was scrolled to when it did
  var last = here(); // where we were before the Back being handled
  // Address another script wrote on one of our entries. A reload lands on such an
  // entry with its address, so start from it.
  var moved = last ? location.href : null;

  /** One of this page's own entries: ours, or the one underneath that reconcile() marked. */
  function ours(state) {
    return !!state && typeof state === 'object' && KEY in state;
  }

  function depthOf(state) {
    return (ours(state) && state[KEY]) || 0;
  }

  function here() {
    return depthOf(history.state);
  }

  // Other scripts keep working on whichever entry is current, which is now often
  // one of ours: filters, sort and the video viewer write the page's address with
  // replaceState, the product card its scroll note. Keep our mark on the entry
  // through that, and remember the address so a Back that closes a layer can put
  // it on the entry underneath too.
  var pushState = history.pushState;
  var replaceState = history.replaceState;
  history.replaceState = function (state, unused, url) {
    if (!ours(history.state)) return replaceState.apply(this, arguments);
    var depth = here();
    var kept = Object.assign({}, state && typeof state === 'object' ? state : null);
    kept[KEY] = depth;
    replaceState.call(this, kept, unused, url);
    if (depth) moved = location.href;
  };
  // The orders page adds entries of its own for its views; they are not spares.
  // Noted after the current task, not in it: the Fastrr checkout adds its entry
  // from inside a popstate listener that can run before ours, and that Back
  // still has to be judged from where it started.
  history.pushState = function () {
    pushState.apply(this, arguments);
    setTimeout(function () { last = here(); }, 0);
  };

  /** A layer inside a drawer keeps its open state if the drawer is closed around it; it only counts while that drawer is open. */
  function shown(el) {
    var host = el.parentElement && el.parentElement.closest('dialog');
    return !host || host.open;
  }

  function modal(dialog) {
    try {
      return dialog.matches(':modal');
    } catch (e) {
      return true; // older WebViews do not know :modal; an open dialog there is treated as one
    }
  }

  function dialogs() {
    order = order.filter(function (d) {
      return d.isConnected && d.open && !closing.has(d);
    });
    return order.filter(shown);
  }

  /** @param {boolean} inner Sheets with their own close method (they sit inside a dialog) or the Escape ones. */
  function sheets(inner) {
    var found = [];
    SHEETS.forEach(function (sheet) {
      if (!!sheet[1] !== inner) return;
      document.querySelectorAll(sheet[0]).forEach(function (el) {
        if (shown(el)) found.push([el, sheet[1]]);
      });
    });
    return found;
  }

  function scrollLocked() {
    var body = document.body;
    return body.style.overflow === 'hidden' || body.style.position === 'fixed' || body.classList.contains('quick-shop-open');
  }

  function openCount() {
    var d = dialogs().length;
    var standalone = sheets(false).length;
    var locked = scrollLocked();
    if (!locked) deaf = false;
    var other = locked && !deaf && !d && !standalone && !closing.size && !document.querySelector('cart-drawer-component dialog[open]');
    return d + sheets(true).length + standalone + (other ? 1 : 0) + (guarded() ? 1 : 0);
  }

  /** The exit guard counts as the bottom layer, on the first page of a visit only. */
  function guarded() {
    return document.body.classList.contains(GUARD) && rooted();
  }

  // In the app, hold the page behind an open layer by fixing <html> in place, and
  // do it before the layer's history entry is added. The layers mostly fix
  // <body>, and the app's shell acts on every history write:
  //   - it undoes a fixed <body> itself and scrolls the page back with a plain
  //     scrollTo, which the theme's smooth scrolling turns into the page sliding
  //     down from its top behind the sheet (0.7s, every sheet, on video);
  //   - it keeps a picture of the screen and shows it on the next Back, pushed
  //     down by how far the page was scrolled when the picture was taken. With
  //     the lock undone that was the full distance: a white screen for 0.3-1.5s.
  // A fixed <html> the shell leaves alone, and the page then reads as scrolled
  // to 0 the whole time a layer is open, so the picture comes back in place.
  // The layers' own closing code still runs; its scrollTo does nothing while
  // <html> is fixed, and the position is put back here once nothing is open.
  function hold() {
    if (!window.includIsApp) return;
    var root = document.documentElement;
    var body = document.body;
    if (!dialogs().length && !sheets(true).length && !sheets(false).length) {
      if (!held) return;
      held = false;
      root.style.position = root.style.top = root.style.width = '';
      window.scrollTo({ top: heldY, behavior: 'instant' });
      return;
    }
    var y = window.scrollY;
    if (body.style.position === 'fixed') {
      y = -parseFloat(body.style.top || '0') || 0;
      body.style.position = body.style.top = body.style.width = '';
    }
    if (held) return; // a layer inside another: the page is already held where the first one found it
    held = true;
    heldY = y;
    root.style.position = 'fixed';
    root.style.top = -y + 'px';
    root.style.width = '100%';
  }

  /** One entry per open layer. Entries are only added: a spare left by an earlier layer is used first. */
  function reconcile() {
    hold();
    var open = openCount();
    var depth = here();
    while (depth < open) {
      var state = Object.assign({}, typeof history.state === 'object' ? history.state : null);
      // Mark the page's own entry too: a Back is only ours to answer when it
      // lands on a marked entry, not on an in-page link's or another script's.
      if (!ours(state)) {
        state[KEY] = 0;
        replaceState.call(history, state, '');
      }
      // Back to an entry underneath must not scroll the page. The browser saves
      // that entry's position now, after the layer has locked the page, so what
      // it would restore is wrong (0 under a fixed body, or wherever the page
      // was when an older layer opened).
      mode = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      state = Object.assign({}, state);
      state[KEY] = depth + 1;
      try {
        pushState.call(history, state, '');
      } catch (e) {
        // Safari refuses more than 100 history calls in 30 seconds.
        history.scrollRestoration = mode;
        break;
      }
      history.scrollRestoration = mode;
      depth++;
    }
    last = depth;
    clearTimeout(giveTimer);
    if (!open && depth && rooted()) giveTimer = setTimeout(giveBack, 300);
  }

  /** The page's own entry is the first of the visit: every entry behind the current one is ours. */
  function rooted() {
    var nav = window.navigation;
    return !!nav && !!nav.currentEntry && nav.currentEntry.index === here();
  }

  // First page of a visit, nothing open, sitting on a spare: step back to the
  // page's own entry so that the next Back leaves (in the app, so that the shell
  // sees there is nowhere to go back to and closes).
  // Waited for and re-checked, never done in the tap that closed the layer: the
  // same tap may open the next layer or start a navigation, and a step back at
  // that moment is what cancelled navigations in Safari. A navigation is seen
  // starting through the Navigation API; without that API rooted() is false and
  // nothing is handed back.
  function giveBack() {
    var depth = here();
    if (!depth || giving || openCount() || !rooted() || Date.now() - leavingAt < 3000) return;
    giving = true;
    // ponytail: if the step never lands, stop expecting it rather than swallow a later Back.
    setTimeout(function () { giving = false; }, 1500);
    history.go(-depth);
  }

  if (window.navigation) {
    navigation.addEventListener('navigate', function (event) {
      if (!event.destination.sameDocument && !event.downloadRequest) leavingAt = Date.now();
    });
  }

  function escape(target) {
    target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
  }

  function closeTop() {
    var inner = sheets(true);
    if (inner.length) return inner[inner.length - 1][1](inner[inner.length - 1][0]);

    var open = dialogs();
    var dialog = open[open.length - 1];
    if (!dialog) {
      // Nothing real is open and the Back used up the exit guard's entry: have
      // the warning shown. Anything that still holds the page comes first.
      if (!sheets(false).length && !(scrollLocked() && !deaf) && guarded()) {
        document.body.dispatchEvent(new CustomEvent('includ:exit-guard'));
        return;
      }
      // An Escape-closed sheet, or something this script does not know that
      // locked the page. Ask it to close, and stop counting a lock that outlives
      // that: if it stays (the Fastrr checkout answers Back itself, not Escape)
      // the next Back must not be swallowed too, and its lock is not ours to undo.
      escape(document);
      deaf = true;
      return;
    }

    closing.add(dialog);
    // ponytail: a dialog that never closes would leave Back disabled for layers
    // under it; stop waiting for it once its animation has had ample time.
    setTimeout(function () { closing.delete(dialog); }, 1500);

    setTimeout(unstick, 700);

    // Stop at an enclosing dialog: a picker inside the bag is not the bag's to
    // close, and closeDialog() there would shut the bag around a still-open picker.
    // And only a host whose own dialog this is: on the /cart page the bag
    // component has no dialog, and its closeDialog() would close nothing.
    for (var el = dialog.parentElement; el && !(el instanceof HTMLDialogElement); el = el.parentElement) {
      if (typeof el.closeDialog === 'function' && el.refs && el.refs.dialog === dialog) return el.closeDialog();
    }
    // A plain <dialog>: its own close button runs whatever clean-up it has (the
    // offers sheet unlocks the page only there). Escape and close() are for the
    // ones without a button.
    var button = dialog.querySelector('[aria-label^="Close" i], [data-close], [class*="__close"]');
    if (button instanceof HTMLElement) return button.click();
    escape(dialog);
    setTimeout(function () {
      if (dialog.open && dialog.dispatchEvent(new Event('cancel', { cancelable: true }))) dialog.close();
    }, 80);
  }

  // ponytail: last resort after a Back. If a dialog was closed without its own
  // clean-up running, the page would stay scroll-locked with nothing open over
  // it; undo the lock the way these layers all apply it.
  function unstick() {
    var body = document.body;
    if (dialogs().length || closing.size || sheets(true).length || sheets(false).length) return;
    if (body.style.position !== 'fixed' && body.style.overflow !== 'hidden') return;
    var y = -parseInt(body.style.top || '0', 10) || 0;
    body.style.position = body.style.top = body.style.width = body.style.overflow = '';
    if (y) window.scrollTo({ top: y, behavior: 'instant' });
  }

  window.addEventListener('popstate', function (event) {
    var from = last;
    // The entry Back landed on, even if a listener before this one has already
    // added another on top of it.
    var at = depthOf(event.state);
    var landed = ours(history.state) && here() === at;
    last = here();
    if (landed) history.scrollRestoration = mode; // the shopper may stay on this entry now
    // Not ours to answer: an in-page link, Forward, or another script's entry.
    if (!ours(event.state) || at >= from) return;

    // The address follows the shopper down, whether this Back closes a layer or
    // passes through a spare (and may stop here, on the first page of a visit).
    if (landed && moved && moved !== location.href) replaceState.call(history, history.state, '', moved);
    if (!at) moved = null;

    // A spare: the layer it was added for was closed with its own button. Nothing
    // to close for this Back, so let it carry on to where it was meant to go.
    if (giving) {
      // Our own step back on the first page; a layer may have opened meanwhile.
      giving = false;
      return reconcile();
    }
    if (openCount() <= at) return history.back();
    closeTop();
  }, true);

  // Restored whole by the browser on Back or Forward, on whichever entry that was.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    last = here();
    if (last) moved = location.href;
    leavingAt = 0;
    reconcile();
  });
  // An in-page link adds an entry without pushState.
  window.addEventListener('hashchange', function () {
    last = here();
  });

  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      var d = m.target;
      if (!(d instanceof HTMLDialogElement)) return;
      if (!d.open) closing.delete(d);
      else if (order.indexOf(d) < 0 && modal(d) && !d.closest('cart-drawer-component')) order.push(d);
    });
    reconcile();
  }).observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ['open'] });

  new MutationObserver(reconcile).observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });

  var sheetObserver = new MutationObserver(reconcile);
  function watch() {
    document.querySelectorAll(WATCH).forEach(function (el) {
      if (watched.has(el)) return;
      watched.add(el);
      sheetObserver.observe(el, { attributes: true, subtree: true, attributeFilter: ['class'] });
    });
  }
  // A dialog shown while the page was still loading, before this script started
  // (the bag, put back on return from the app's checkout), never reaches the
  // observer above.
  document.querySelectorAll('dialog[open]').forEach(function (d) {
    if (modal(d) && !d.closest('cart-drawer-component')) order.push(d);
  });
  watch();
  reconcile(); // reloaded onto a spare on the first page of a visit
  // Sheets arrive late (sections re-render, quick shop is built on first use) and
  // are opened by a tap; look again after each one.
  document.addEventListener('click', function () {
    setTimeout(function () { watch(); reconcile(); }, 0);
  }, true);
})();
